import { LocationsApiTypes, referencesApi, ReferencesApiTypes } from 'api'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  LOCATION_TABS,
  LOCATION_ADDING_FORM_VALUES,
} from 'texts/locationDetails'
import { ContactInformationForm } from './components/ContactInformationForm'
import {
  userDataSelector,
  isOperatorSelector,
  isMigratedUserFlowSelector,
} from 'redux/login/selectors'
import { LocationsTable } from '../LocationsTable'
import { ContactInformationFormProps } from './components/ContactInformationForm/types'
import { useAppSelector } from 'redux/hooks'
import { ACCOUNT_STATUSES, REGISTRATION_STATUSES } from 'api/auth/constants'
import { WaitingHoursForm } from './components/WaitingHoursForm'
import { WorkingHoursForm } from './components/WorkingHoursForm'
import { Form } from '@frontend/design_system'
import { LeftSide } from './components/LeftSide'
import { RightSide } from './components/RightSide'
import {
  FormValues,
  LocationDetailsFormProps,
  WorkingHoursErrors,
} from './types'
import { initialFormValues, WORKING_HOURS_INITIAL_VALUES } from './constants'
import {
  PickupOptions,
  WaitingHourFeeType,
  WaitingHourRange,
} from 'api/locations/types'
import { Location, useSelectLocation } from '../../hooks/useSelectLocation'
import countryOfResidence from 'constants/countryOfResidence'
import { ReservationForm } from './components/ReservationForm'
import { useDidUpdate } from 'hooks/useDidUpdate'
import { useDriverAgeRangeValidation } from 'ui/components/DriverAgeRanges/hooks/useDriverAgeRangeValidation'
import styles from './styles.module.scss'
import { useDispatch } from 'react-redux'
import { setMapData, setNewLocationError } from 'redux/locations/slice'
import { LOCATION_TYPE_AIRPORT } from './components/ContactInformationForm/constants'
import {
  citiesSelector,
  locationsSelector,
  locationTypeSelector,
  newLocationErrorSelector,
} from 'redux/locations/selectors'
import { WeekDayErrors } from './components/WorkingHoursForm/components/WeekDay/types'
import { checkWorkingHourErrorsExist } from './utils'
import { FormChildren } from 'types/form'
import { useApiRequest } from 'hooks/useApiRequest'
import { Loader } from 'ui/atoms/Loader'
import { ApprovalInfoBadges } from 'ui/components/ApprovalInfoBadges'
import { UnsavedChangesModal } from 'ui/components/UnsavedChangesModal'
import { TypeOfLocation } from 'api/references/types'
import { MigratedUserBadge } from 'ui/components/MigratedUserBadge'
import { scrollTop } from 'utils/scrollTop'

export const LocationDetailsForm = ({
  handleSubmit,
  formValues,
  isLoading,
  isEditing,
  error,
  formId,
  resetError,
}: LocationDetailsFormProps) => {
  const dispatch = useDispatch()
  const {
    latitude,
    longitude,
    phone,
    street,
    waitingHours,
    postalCode,
    waitingHourPrice,
    building,
    countryId,
    cityId,
    locationId,
    email,
    minimumPeriodDay,
    maximumPeriodDay,
    pickupOption,
    pickUpInstruction,
    dropOffInstruction,
    driverAgeRange,
    startReservationHour,
    currencyName,
    waitingHourFeeType,
  } = initialFormValues
  const workingHoursFormRef = useRef<HTMLDivElement>(null)
  const [formKey, setFormKey] = useState(0)
  const { accountStatus, currency, sectionsStatuses } =
    useAppSelector(userDataSelector)
  const isOperator = useAppSelector(isOperatorSelector)
  const locationType = useAppSelector(locationTypeSelector)
  const cities = useAppSelector(citiesSelector)
  const locations = useAppSelector(locationsSelector)
  const newLocationError = useAppSelector(newLocationErrorSelector)
  const isMigratedUserFlow = useAppSelector(isMigratedUserFlowSelector)
  const [locationTypes, setLocationTypes] = useState<
    ReferencesApiTypes.ListItemType[]
  >([])
  const [workingHoursFormKey, setWorkingHoursFormKey] = useState(0)
  const [workingHourErrors, setWorkingHourErrors] =
    useState<WorkingHoursErrors>({})
  const showLimitInfoAndTable =
    !isOperator && accountStatus === ACCOUNT_STATUSES.REGISTERED
  const [isWorkingHoursChanged, setWorkingHoursChanged] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [workingHours, setWorkingHours] =
    useState<LocationsApiTypes.WorkingHours>(
      WORKING_HOURS_INITIAL_VALUES.map(
        (workHour) =>
          formValues?.workingHours?.find(
            ({ dayOfWeek }) => dayOfWeek === workHour.dayOfWeek
          ) || workHour
      )
    )
  const [currentLocation, setCurrentLocation] = useState<Location>({
    country: {
      name: formValues?.countryName || '',
      id: formValues?.countryId || countryId,
    },
    state: {
      name: formValues?.stateName || '',
      id: formValues?.stateId || '',
    },
    city: {
      name: formValues?.cityName || '',
      id: formValues?.cityId || cityId,
    },
    location: {
      name: formValues?.locationName || '',
      id: formValues?.locationId || locationId,
    },
  })
  const [isFormInitialized, setIsFormInitialized] = useState(false)
  const [activeKey, setActiveKey] = useState(LOCATION_TABS.INFORMATION)

  const [workingHoursError, setWorkingHoursError] = useState('')
  const [driverAgeRangeError, setDriverAgeRangeError] = useState('')

  const isPendingUser =
    !isOperator &&
    sectionsStatuses?.locationsStatus !== REGISTRATION_STATUSES.APPROVED

  const locationTypesRequest = useApiRequest(() =>
    referencesApi.getLocationTypes()
  )

  const onWorkingHoursUpdate = () => {
    setWorkingHoursFormKey(workingHoursFormKey + 1)
  }

  const handleTabChange = (activeKey: string) => {
    setActiveKey(activeKey)
    onWorkingHoursUpdate()
  }

  const handleChangeLocation = (
    location: ContactInformationFormProps['currentLocation']
  ) => {
    setCurrentLocation(location)
  }

  useEffect(() => {
    dispatch(setMapData(null))

    const getLocationTypes = async () => {
      const response = await locationTypesRequest.apiRequest()
      if (response) {
        setLocationTypes(response.data)
      }
    }

    getLocationTypes()
  }, [])

  useEffect(() => {
    if (newLocationError) {
      setActiveKey(LOCATION_TABS.INFORMATION)
    }

    return () => {
      dispatch(setNewLocationError(''))
    }
  }, [newLocationError])

  const checkDriverAgeRange = useDriverAgeRangeValidation(true)

  const handleResetError = (dayOfWeek: string, key: string) => {
    setWorkingHourErrors((prev) => ({
      ...prev,
      [dayOfWeek]: { ...prev[dayOfWeek], [key]: false, errorMessage: '' },
    }))
  }

  const prepareWorkingHours = () => {
    let invalidDate = false
    let workingHoursOverlap = false
    let incompleteInfo = false
    let workingHoursPairsOverlap = false

    const onChangeErrors = (dayOfWeek: string, errors: WeekDayErrors) => {
      setWorkingHourErrors((prev) => ({
        ...prev,
        [dayOfWeek]: {
          ...prev[dayOfWeek],
          ...errors,
        },
      }))
    }

    const onChangeErrorMessage = (dayOfWeek: string, errorMessage: string) => {
      setWorkingHourErrors((prev) => ({
        ...prev,
        [dayOfWeek]: {
          ...prev[dayOfWeek],
          errorMessage,
        },
      }))
    }

    const initialErrors = {
      dayStart: false,
      dayEnd: false,
      dayStart2: false,
      dayEnd2: false,
    }

    const checkWorkingHours = () =>
      workingHours.filter(
        ({ dayStart, dayEnd, dayStart2, dayEnd2, dayOfWeek }) => {
          const getDateHours = (time: string) => {
            const [hour, minute] = time.split(':')

            return new Date().setHours(+hour, +minute)
          }

          if (!dayStart && !dayEnd && !dayStart2 && !dayEnd2) {
            onChangeErrors(dayOfWeek, {
              ...initialErrors,
              errorMessage: '',
            })
          }

          if (!dayStart && !dayEnd) {
            onChangeErrors(dayOfWeek, {
              dayStart: false,
              dayEnd: false,
            })
          }

          if (!dayStart2 && !dayEnd2) {
            onChangeErrors(dayOfWeek, {
              dayStart2: false,
              dayEnd2: false,
            })
          }

          if ((dayStart && dayEnd) || (dayStart2 && dayEnd2)) {
            if (dayStart && dayEnd) {
              if (getDateHours(dayEnd) - getDateHours(dayStart) <= 0) {
                invalidDate = true
                onChangeErrorMessage(
                  dayOfWeek,
                  LOCATION_ADDING_FORM_VALUES.WORKING_HOURS.VALIDATION_MESSAGE
                )
                onChangeErrors(dayOfWeek, {
                  ...initialErrors,
                  dayStart: true,
                  dayEnd: true,
                })
              } else {
                onChangeErrors(dayOfWeek, { dayStart: false, dayEnd: false })
              }
            }

            if (dayStart2 && dayEnd2) {
              if (getDateHours(dayEnd2) - getDateHours(dayStart2) <= 0) {
                invalidDate = true
                onChangeErrorMessage(
                  dayOfWeek,
                  LOCATION_ADDING_FORM_VALUES.WORKING_HOURS.VALIDATION_MESSAGE
                )
                onChangeErrors(dayOfWeek, {
                  dayStart2: true,
                  dayEnd2: true,
                })
              } else {
                onChangeErrors(dayOfWeek, { dayStart2: false, dayEnd2: false })
              }
            }

            if (invalidDate) {
              return
            }
          }

          if (
            (dayStart && !dayEnd) ||
            (!dayStart && dayEnd) ||
            (dayStart2 && !dayEnd2) ||
            (!dayStart2 && dayEnd2)
          ) {
            incompleteInfo = true
            onChangeErrorMessage(
              dayOfWeek,
              LOCATION_ADDING_FORM_VALUES.WORKING_HOURS.INCOMPLETE_INFORMATION
            )
            onChangeErrors(dayOfWeek, { ...initialErrors })
            if (dayStart && !dayEnd) onChangeErrors(dayOfWeek, { dayEnd: true })
            if (!dayStart && dayEnd)
              onChangeErrors(dayOfWeek, { dayStart: true })
            if (dayStart2 && !dayEnd2)
              onChangeErrors(dayOfWeek, { dayEnd2: true })
            if (!dayStart2 && dayEnd2)
              onChangeErrors(dayOfWeek, { dayStart2: true })

            return
          }

          if (dayStart && dayEnd && dayStart2 && dayEnd2) {
            if (dayEnd > dayStart2 && dayEnd2 > dayStart) {
              workingHoursOverlap = true
              onChangeErrorMessage(
                dayOfWeek,
                LOCATION_ADDING_FORM_VALUES.WORKING_HOURS.OVERLAP_MESSAGE
              )
              if (!(dayEnd < dayStart2)) {
                onChangeErrors(dayOfWeek, { dayStart2: true, dayEnd: true })
              } else {
                onChangeErrors(dayOfWeek, { dayStart2: false, dayEnd: false })
              }

              return
            }

            if (
              dayEnd <= dayStart ||
              dayStart2 <= dayEnd ||
              dayEnd2 <= dayStart2
            ) {
              workingHoursPairsOverlap = true
              onChangeErrorMessage(
                dayOfWeek,
                LOCATION_ADDING_FORM_VALUES.WORKING_HOURS.PAIR_OVRELAP_MESSAGE
              )

              onChangeErrors(dayOfWeek, {
                dayStart: true,
                dayEnd: true,
                dayStart2: true,
                dayEnd2: true,
              })

              return
            }
          }
          return dayStart && dayEnd
        }
      )

    const result = checkWorkingHours()

    if (!result.length) {
      setWorkingHoursError(
        LOCATION_ADDING_FORM_VALUES.WORKING_HOURS.REQUIRED_MESSAGE
      )

      return []
    } else if (
      invalidDate ||
      incompleteInfo ||
      workingHoursOverlap ||
      workingHoursPairsOverlap
    ) {
      return []
    }

    return result
  }

  const validateWorkingHours = () => {
    const preparedWorkingHours = prepareWorkingHours()

    if (!preparedWorkingHours.length) {
      workingHoursFormRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
    return preparedWorkingHours
  }

  const validateDriverAgeRange = (data: Partial<FormValues>) => {
    const driverAgeRangeError = checkDriverAgeRange(data?.driverAgeRange)
    if (driverAgeRangeError) {
      setDriverAgeRangeError(driverAgeRangeError)
      scrollTop()
    }
    return driverAgeRangeError
  }

  const onSubmit = (data: Partial<FormValues>, validate: boolean) => {
    const preparedWorkingHours = validateWorkingHours()
    const driverAgeRangeError = validateDriverAgeRange(data)
    onWorkingHoursUpdate()
    setIsSubmitting(false)

    if (validate && preparedWorkingHours.length && !driverAgeRangeError) {
      setIsSubmitting(true)
      const currentCityId = cities.find(
        (city) => city.label === data.cityName
      )?.value
      const currentLocationId = locations.find(
        (location) => location.label === data.locationName
      )?.value
      handleSubmit({
        ...data,
        locationId: currentLocationId,
        cityId: currentCityId,
        countryId: currentLocation.state.id
          ? String(currentLocation.state.id)
          : data.countryId,
        phoneAreaCode: data.phoneCor?.code
          ? String(data.phoneCor?.phoneCode)
          : '',
        phoneCountryCode: data.phoneCor?.code
          ? String(data.phoneCor?.code)
          : '',
        dropoffHours: preparedWorkingHours,
        workingHours: preparedWorkingHours,
        newLocationType: undefined,
        pickupOption:
          locationType === LOCATION_TYPE_AIRPORT
            ? (data.pickupOption as PickupOptions)
            : undefined,
        newLocation: currentLocationId
          ? undefined
          : {
              locationName: data.locationName,
              locationType: data.newLocationType,
              pendingCityId: currentCityId ? Number(currentCityId) : undefined,
              cityName: data.cityName,
              shortCode: data.shortCode,
            },
      })
    }
  }

  const initValues = useMemo(
    () => ({
      latitude: String(formValues?.latitude ?? latitude),
      longitude: String(formValues?.longitude ?? longitude),
      countryId: String(currentLocation.country.id),
      cityId: String(currentLocation.city.id),
      cityName: String(currentLocation.city.name),
      stateId: String(currentLocation.state.id),
      locationId: String(currentLocation.location.id),
      locationName: String(currentLocation.location.name),
      building: formValues?.building || building,
      phone: formValues?.phone || phone,
      phoneCor: countryOfResidence.find(
        (el) => el.code === formValues?.phoneCountryCode
      ),
      street: formValues?.street || street,
      waitingHours: String(
        formValues?.waitingHours?.waitingHours || waitingHours
      ),
      waitingHourPrice: String(
        formValues?.waitingHours?.waitingHourPrice || waitingHourPrice
      ),
      waitingHourRange:
        formValues?.waitingHours?.waitingHourRange || WaitingHourRange.Both,
      waitingHourFeeType:
        formValues?.waitingHours?.waitingHourFeeType || waitingHourFeeType,
      postalCode: formValues?.postalCode || postalCode,
      currencyName: formValues?.currencyName || currency || currencyName,
      email: formValues?.email || email,
      minimumPeriodDay: formValues?.minimumPeriodDay
        ? String(formValues?.minimumPeriodDay)
        : minimumPeriodDay,
      maximumPeriodDay: formValues?.maximumPeriodDay
        ? String(formValues?.maximumPeriodDay)
        : maximumPeriodDay,
      pickupOption: formValues?.pickupOption || pickupOption,
      startReservationHour: formValues?.startReservationHour
        ? String(formValues?.startReservationHour)
        : startReservationHour,
      pickUpInstruction: formValues?.pickUpInstruction || pickUpInstruction,
      dropOffInstruction: formValues?.dropOffInstruction || dropOffInstruction,
      driverAgeRange: formValues?.driverAgeRange || driverAgeRange,
      pendingLocationId: formValues?.pendingLocationId,
      pendingCityId: formValues?.pendingCityId,
      shortCode: formValues?.shortCode || '',
    }),
    [currentLocation, formValues]
  )

  const handleWorkingHoursChange = (
    workingHours: LocationsApiTypes.WorkingHours
  ) => {
    setWorkingHoursError('')
    setWorkingHoursChanged(true)
    setWorkingHours(workingHours)
  }

  const handleResetDriverAgeError = () => setDriverAgeRangeError('')

  const locationData = useSelectLocation(
    currentLocation,
    handleChangeLocation,
    currentLocation.location.id,
    formValues?.pendingLocationId,
    formValues?.pendingCityId,
    (locationType || formValues?.locationType) as TypeOfLocation
  )

  useEffect(() => {
    if (isMigratedUserFlow) {
      validateDriverAgeRange({ driverAgeRange: formValues?.driverAgeRange })
    }
  }, [formValues, isMigratedUserFlow])

  const locationTabs = (values: Record<string, unknown>) => [
    {
      tab: LOCATION_TABS.INFORMATION,
      key: LOCATION_TABS.INFORMATION,
      children: (
        <ContactInformationForm
          locationData={locationData}
          currentLocation={currentLocation}
          changeActiveKey={setActiveKey}
          values={values}
          locationTypes={locationTypes}
          isEditing={!!isEditing}
          isFormInitialized={isFormInitialized}
        />
      ),
    },
    {
      tab: LOCATION_TABS.WORKING_HOURS,
      key: LOCATION_TABS.WORKING_HOURS,
      children: (
        <WorkingHoursForm
          workingHours={workingHours}
          onWorkingHoursChange={handleWorkingHoursChange}
          error={workingHoursError}
          ref={workingHoursFormRef}
          formKey={workingHoursFormKey}
          errors={workingHourErrors}
          onWorkingHoursUpdate={onWorkingHoursUpdate}
          resetError={handleResetError}
          changeActiveKey={setActiveKey}
          validateWorkingHours={validateWorkingHours}
        />
      ),
    },
    {
      tab: LOCATION_TABS.RESERVATION,
      key: LOCATION_TABS.RESERVATION,
      children: (
        <ReservationForm
          formValues={values}
          driverAgeRangeError={driverAgeRangeError}
          resetDriverAgeError={handleResetDriverAgeError}
          changeActiveKey={setActiveKey}
          validateDriverAgeRange={validateDriverAgeRange}
          validateWorkingHours={validateWorkingHours}
        />
      ),
    },
    {
      tab: LOCATION_TABS.WAITING_HOURS,
      key: LOCATION_TABS.WAITING_HOURS,
      children: (
        <WaitingHoursForm
          withFee={values.waitingHourFeeType === WaitingHourFeeType.Fee}
        />
      ),
    },
  ]

  useDidUpdate(() => {
    if (error && resetError) {
      setFormKey((prev) => prev + 1)
      setCurrentLocation({
        country: {
          name: '',
          id: '',
        },
        city: {
          name: '',
          id: '',
        },
        location: {
          name: '',
          id: '',
        },
        state: {
          name: '',
          id: '',
        },
      })
      setWorkingHours(
        WORKING_HOURS_INITIAL_VALUES.map(
          (workHour) =>
            formValues?.workingHours?.find(
              ({ dayOfWeek }) => dayOfWeek === workHour.dayOfWeek
            ) || workHour
        )
      )
      setWorkingHoursChanged(false)
      resetError()
    }
  }, [error])

  return (
    <>
      {locationData.isLoaded || !isEditing ? (
        <Form
          initValues={initValues}
          onSubmit={onSubmit}
          key={formKey}
          formId={formId}
          isMountValidate={isEditing && isMigratedUserFlow}
          reduxId={formId}
          className={styles['form-wrapper']}
        >
          {({ values, isDirty, errors }: FormChildren) => (
            <>
              <MigratedUserBadge errors={errors} />
              {isPendingUser && (
                <ApprovalInfoBadges
                  isRequested={
                    sectionsStatuses?.locationsStatus ===
                    REGISTRATION_STATUSES.REQUESTED
                  }
                />
              )}
              <UnsavedChangesModal isDirty={isDirty && !isSubmitting} />
              <div className={styles.container}>
                <LeftSide
                  changeActiveKey={handleTabChange}
                  activeKey={activeKey}
                  tabs={locationTabs(values)}
                  driverAgeRangeError={driverAgeRangeError}
                  workingHoursError={
                    !!workingHoursError ||
                    checkWorkingHourErrorsExist(workingHourErrors)
                  }
                  onChangeInitialized={() => setIsFormInitialized(true)}
                />
                <RightSide
                  activeKey={activeKey}
                  formValues={values}
                  isDirty={!!isDirty}
                  isWorkingHoursChanged={isWorkingHoursChanged}
                  workingHours={workingHours}
                  isLoading={isLoading}
                  isEditing={isEditing}
                />
              </div>
              {showLimitInfoAndTable && <LocationsTable />}
            </>
          )}
        </Form>
      ) : (
        <Loader offsetTop={160} />
      )}
    </>
  )
}
